import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { DeploymentUnitOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { useUserPermit } from '../../context/User.context';
import BookstatDataFlow from './components/BookstatDataFlow';
import PipelineGrid from './components/PipelineGrid';
import PipelineDetailDrawer from './components/PipelineDetailDrawer';
import { BRENDA_GREEN } from './utils/statusColors';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const TitleAccent = styled(DeploymentUnitOutlined)`
  color: ${BRENDA_GREEN};
  font-size: 20px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
`;

const Footer = styled.div`
  text-align: center;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 8px;
`;

const NoAccess = styled.div`
  padding: 48px;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
`;

const PipelineProgressSection: React.FC = () => {
  const userPermit = useUserPermit();
  const canView = userPermit?.role && ['Super Admin', 'Acquisitions Lead'].includes(userPermit.role);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleSelect = useCallback((id: string) => setSelectedId(id), []);
  const handleClose = useCallback(() => setSelectedId(null), []);
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries(['pipelines']);
  }, [queryClient]);

  if (!canView) {
    return <NoAccess>Pipeline Observability Dashboard is restricted to admin roles.</NoAccess>;
  }

  return (
    <Wrapper>
      <TopBar>
        <Heading>
          <TitleRow>
            <SectionTitle>Pipeline Observability Dashboard</SectionTitle>
            <TitleAccent />
          </TitleRow>
          <Subtitle>
            Live status across model training, ETL, inference, and scheduled timers. Auto-refreshes every 5 minutes.
          </Subtitle>
        </Heading>
        <Button
          type='primary'
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          style={{ background: BRENDA_GREEN, borderColor: BRENDA_GREEN }}
        >
          Refresh now
        </Button>
      </TopBar>
      <BookstatDataFlow />
      <PipelineGrid onSelect={handleSelect} />
      <PipelineDetailDrawer pipelineId={selectedId} onClose={handleClose} />
      <Footer>All times shown in your local timezone · Auto-refreshed every 5 minutes</Footer>
    </Wrapper>
  );
};

export default PipelineProgressSection;
